import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ShopHours, ShopHoursSpec } from '@popcart/core/v1';
import { api, errorMessage } from '../../api';
import { Loading, Segmented, StatusPill, useNow, useTitle } from '../../ui/bits';
import { SceneError } from '../../catalog/art';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { shopKeys, useShop } from '../ShopLayout';
import {
  DAYS,
  DEFAULT_WINDOW,
  MAX_WINDOWS,
  PAUSE_OPTIONS,
  fromDraft,
  hoursActions,
  hoursDirty,
  hoursSummary,
  pausedToast,
  statusView,
  toDraft,
  type DayKey,
  type HoursDraft,
  type WindowDraft,
} from './lib/hours.js';
import { hoursKeys } from './keys';
import '../shop.css';
import './hours.css';
import { TimeField } from '../../ui/controls';

type PauseMinutes = (typeof PAUSE_OPTIONS)[number]['minutes'];

/** /shop/:shopId/hours — weekly hours, the Taking orders switch, and short pauses. */
export function ShopHoursPage() {
  useTitle('Hours & pausing');
  const { shopId } = useShop();
  const hours = useQuery({ queryKey: hoursKeys.hours(shopId), queryFn: () => api.shopHours(shopId), refetchInterval: 30_000, enabled: !!shopId });

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Hours &amp; pausing</h1>
          <span className="dv-live">{hours.data ? hoursSummary(hours.data.hours) : 'When customers can order'}</span>
        </div>
      </header>
      {hours.isError && !hours.data ? (
        <section className="card">
          <SceneError error={hours.error} onRetry={() => void hours.refetch()} />
        </section>
      ) : !hours.data ? (
        <Loading label="Loading your hours" rows={2} />
      ) : (
        <div className="hr-grid">
          <StatusCard shopId={shopId} h={hours.data} />
          <WeekEditor key={hours.data.shopId} shopId={shopId} saved={hours.data.hours} />
        </div>
      )}
    </>
  );
}

function useHoursWrite(shopId: string) {
  const qc = useQueryClient();
  return (h: ShopHours) => {
    qc.setQueryData(hoursKeys.hours(shopId), h);
    // Today shows OPEN / CLOSED and its own Taking orders switch: keep it in step.
    void qc.invalidateQueries({ queryKey: shopKeys.today(shopId) });
  };
}

function StatusCard({ shopId, h }: { shopId: string; h: ShopHours }) {
  const now = useNow(15_000);
  const toast = useToast();
  const write = useHoursWrite(shopId);
  const actions = useMemo(() => hoursActions(api, shopId), [shopId]);
  const v = statusView(h, now);

  const taking = useMutation({
    mutationFn: (on: boolean) => actions.setTaking(on),
    onSuccess: (res, on) => {
      write(res);
      toast(on ? 'Taking orders again.' : 'Orders off. Customers see you as closed until you switch it back on.');
    },
    onError: (err) => toast(errorMessage(err, "Couldn't change that. Try again."), 'error'),
  });
  const pause = useMutation({
    mutationFn: (m: PauseMinutes | 0) => (m === 0 ? actions.resume() : actions.pause(m)),
    onSuccess: (res, m) => {
      write(res);
      toast(m === 0 ? 'Back on. Taking orders again.' : pausedToast(m, res.pausedUntil));
    },
    onError: (err) => toast(errorMessage(err, "Couldn't pause. Try again."), 'error'),
  });
  const busy = taking.isPending || pause.isPending;

  return (
    <section className="card dv-card hr-status dv-rise" aria-labelledby="hr-status-h">
      <div className="dv-card-h">
        <h2 id="hr-status-h">Right now</h2>
        <span className="grow" />
        <StatusPill tone={v.open ? 'success' : 'neutral'}>{v.title}</StatusPill>
      </div>
      <div className={`hr-now${v.taking ? '' : ' off'}${v.reason === 'paused' ? ' paused' : ''}`} role="status">
        <span className="hr-now-ic" aria-hidden="true">
          <Icon name={v.taking ? 'check' : v.reason === 'paused' ? 'clock' : 'alert'} />
        </span>
        <span className="stack s4">
          <b className="hr-now-t">{v.taking ? 'Open for orders' : v.reason === 'paused' ? 'Paused' : v.reason === 'off' ? 'Orders off' : 'Closed'}</b>
          <span className="dv-sub">{v.taking ? 'Customers can order now' : v.reason === 'paused' ? 'Customers can’t order right now.' : v.detail}</span>
        </span>
      </div>

      <button
        type="button"
        role="switch"
        className={`ph-open press hr-taking${h.acceptingOrders ? ' on' : ''}`}
        aria-checked={h.acceptingOrders}
        aria-describedby="hr-taking-help"
        disabled={busy}
        onClick={() => taking.mutate(!h.acceptingOrders)}
      >
        <span className="track" aria-hidden="true" />
        <b>Taking orders</b>
        <span id="hr-taking-help" className="sr-only">
          {h.acceptingOrders ? 'Switch off to close until you switch it back' : 'Off · customers can’t order'}
        </span>
      </button>

      <div className="stack hr-pause">
        <span className="dv-label" id="hr-pause-l">
          Swamped? Pause
        </span>
        {v.reason === 'paused' ? (
          <div className="row wrap hr-paused">
            <StatusPill tone="warning">{v.detail}</StatusPill>
            <button type="button" className="btn btn-secondary" disabled={busy} aria-busy={pause.isPending || undefined} onClick={() => pause.mutate(0)}>
              {pause.isPending ? 'Saving…' : 'Resume now'}
            </button>
          </div>
        ) : null}
        <div className="hr-pauses" role="group" aria-labelledby="hr-pause-l">
          {PAUSE_OPTIONS.map((o) => (
            <button
              key={o.minutes}
              type="button"
              className="btn btn-quiet"
              disabled={busy || !h.acceptingOrders}
              aria-busy={(pause.isPending && pause.variables === o.minutes) || undefined}
              title="Customers see “Back at …” and can’t order until the pause ends. Orders already in keep going."
              onClick={() => pause.mutate(o.minutes)}
            >
              <span className="sr-only">Pause </span>
              {o.label}
            </button>
          ))}
        </div>
        {h.acceptingOrders ? null : <span className="dv-sub">Switch Taking orders on to use a pause.</span>}
      </div>
    </section>
  );
}

const MODES: { value: HoursDraft['mode']; label: string }[] = [
  { value: 'daily', label: 'Same hours every day' },
  { value: 'perDay', label: 'Hours per day' },
  { value: 'always', label: 'Always open' },
];

function WeekEditor({ shopId, saved }: { shopId: string; saved: ShopHoursSpec }) {
  const toast = useToast();
  const write = useHoursWrite(shopId);
  const actions = useMemo(() => hoursActions(api, shopId), [shopId]);
  const [draft, setDraft] = useState<HoursDraft>(() => toDraft(saved));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const savedKey = JSON.stringify(saved);
  useEffect(() => {
    setDraft(toDraft(saved));
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);
  const dirty = hoursDirty(draft, saved);

  const save = useMutation({
    mutationFn: (spec: ShopHoursSpec) => actions.saveHours(spec),
    onSuccess: (res) => {
      write(res);
      toast('Hours saved. Customers see them now.');
    },
    onError: (err) => toast(errorMessage(err, "Couldn't save your hours. Try again."), 'error'),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const r = fromDraft(draft);
    if (!r.ok) {
      setErrors(r.errors);
      return;
    }
    setErrors({});
    save.mutate(r.spec);
  };

  const setDay = (key: DayKey, fn: (d: HoursDraft['days'][DayKey]) => HoursDraft['days'][DayKey]) =>
    setDraft((d) => ({ ...d, days: { ...d.days, [key]: fn(d.days[key]) } }));

  return (
    <form className="card dv-card hr-week dv-rise" style={{ ['--i' as string]: 1 }} aria-labelledby="hr-week-h" onSubmit={onSubmit} noValidate>
      <div className="dv-card-h">
        <h2 id="hr-week-h">Weekly hours</h2>
        {dirty ? <span className="dv-chip">Unsaved</span> : null}
      </div>

      <WeekStrip draft={draft} />

      <div className="hr-mode">
        <Segmented label="How your hours work" value={draft.mode} options={MODES} onChange={(mode) => setDraft((d) => ({ ...d, mode }))} />
      </div>

      {draft.mode === 'always' ? (
        <p className="dv-quiet">
          <Icon name="check" />
          Open any time you're Taking orders
        </p>
      ) : draft.mode === 'daily' ? (
        <div className="stack">
          <WindowRow id="hr-daily" label="Every day" w={draft.daily} error={errors.daily} onChange={(w) => setDraft((d) => ({ ...d, daily: w }))} />
          <span className="dv-sub">Can run past midnight</span>
        </div>
      ) : (
        <div className="stack hr-days">
          {errors.days ? <span className="pill st-error err-pill">{errors.days}</span> : null}
          {DAYS.map((day) => {
            const d = draft.days[day.key];
            return (
              <div key={day.key} className="hr-day" role="group" aria-labelledby={`hr-${day.key}-l`}>
                <div className="hr-day-head">
                  <b id={`hr-${day.key}-l`} className="t-label">
                    {day.label}
                  </b>
                  <button
                    type="button"
                    role="switch"
                    className="switch"
                    aria-checked={!d.closed}
                    aria-label={`${day.label} open`}
                    onClick={() => setDay(day.key, (x) => ({ ...x, closed: !x.closed, windows: x.windows.length ? x.windows : [{ ...DEFAULT_WINDOW }] }))}
                  >
                    <span className="track" aria-hidden="true" />
                    <span className="t-caption">{d.closed ? 'Closed' : 'Open'}</span>
                  </button>
                </div>
                {d.closed ? (
                  <span className="dv-sub hr-closed">Closed all day</span>
                ) : (
                  <div className="stack">
                    {d.windows.map((w, i) => (
                      <WindowRow
                        key={i}
                        id={`hr-${day.key}-${i}`}
                        label={i === 0 ? `${day.short} hours` : `${day.short} second window`}
                        w={w}
                        error={errors[`${day.key}.${i}`]}
                        onChange={(nw) => setDay(day.key, (x) => ({ ...x, windows: x.windows.map((o, j) => (j === i ? nw : o)) }))}
                        onRemove={i > 0 ? () => setDay(day.key, (x) => ({ ...x, windows: x.windows.filter((_, j) => j !== i) })) : undefined}
                      />
                    ))}
                    {errors[day.key] ? <span className="pill st-error err-pill">{errors[day.key]}</span> : null}
                    {d.windows.length < MAX_WINDOWS ? (
                      <button
                        type="button"
                        className="btn btn-quiet hr-add"
                        onClick={() => setDay(day.key, (x) => ({ ...x, windows: [...x.windows, { open: '17:00', close: '21:00' }] }))}
                      >
                        <Icon name="plus" size="sm" />
                        Second window
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="row wrap hr-actions">
        <span className="dv-sub grow">{dirty ? 'Unsaved changes.' : 'Saved.'}</span>
        <button
          type="button"
          className="btn btn-quiet"
          disabled={!dirty || save.isPending}
          onClick={() => {
            setDraft(toDraft(saved));
            setErrors({});
          }}
        >
          Undo changes
        </button>
        <button type="submit" className="btn btn-primary" disabled={!dirty || save.isPending} aria-busy={save.isPending || undefined}>
          {save.isPending ? 'Saving…' : 'Save hours'}
        </button>
      </div>
    </form>
  );
}

const toMin = (hm: string) => {
  const [h = 0, m = 0] = hm.split(':').map(Number);
  return Math.max(0, Math.min(1440, h * 60 + m));
};

/** The week as seven 24-hour tracks, filled where the shop is open (live as you edit). */
function WeekStrip({ draft }: { draft: HoursDraft }) {
  const today = (new Date().getDay() + 6) % 7; // Monday first
  const spans = (key: DayKey): [number, number][] => {
    if (draft.mode === 'always') return [[0, 1440]];
    const ws = draft.mode === 'daily' ? [draft.daily] : draft.days[key].closed ? [] : draft.days[key].windows;
    return ws.flatMap((w): [number, number][] => {
      const a = toMin(w.open);
      const b = toMin(w.close);
      if (!w.open || !w.close || a === b) return [];
      return b > a ? [[a, b]] : [[a, 1440], [0, b]];
    });
  };
  return (
    <div className="hr-strip" role="img" aria-label="Opening hours this week">
      <div className="hr-ticks" aria-hidden="true">
        <span />
        <span className="hr-axis">
          {['12a', '6a', '12p', '6p', '12a'].map((t, i) => (
            <i key={i} style={{ left: `${i * 25}%` }}>
              {t}
            </i>
          ))}
        </span>
      </div>
      {DAYS.map((d, i) => {
        const sp = spans(d.key);
        const mins = sp.reduce((s, [a, b]) => s + (b - a), 0);
        return (
          <div key={d.key} className={`hr-srow${i === today ? ' today' : ''}`} aria-hidden="true">
            <span className="hr-sday">{d.short}</span>
            <span className="hr-track">
              {sp.map(([a, b], j) => (
                <i key={j} style={{ left: `${(a / 1440) * 100}%`, width: `${((b - a) / 1440) * 100}%`, ['--j' as string]: i }} />
              ))}
            </span>
            <span className="hr-sh">{mins ? `${Math.round((mins / 60) * 10) / 10}h` : '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

function WindowRow({ id, label, w, error, onChange, onRemove }: { id: string; label: string; w: WindowDraft; error?: string; onChange(w: WindowDraft): void; onRemove?: () => void }) {
  return (
    <div className="stack s4">
      <div className="row wrap hr-window">
        <div className="field">
          <label htmlFor={`${id}-open`} className="sr-only">
            {label}: opens
          </label>
          <span className="dv-label" aria-hidden="true">
            Opens
          </span>
          <TimeField id={`${id}-open`} className="input hr-time" value={w.open} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-err` : undefined} onChange={(e) => onChange({ ...w, open: e.target.value })} />
        </div>
        <span className="hr-to" aria-hidden="true">
          to
        </span>
        <div className="field">
          <label htmlFor={`${id}-close`} className="sr-only">
            {label}: closes
          </label>
          <span className="dv-label" aria-hidden="true">
            Closes
          </span>
          <TimeField id={`${id}-close`} className="input hr-time" value={w.close} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-err` : undefined} onChange={(e) => onChange({ ...w, close: e.target.value })} />
        </div>
        {onRemove ? (
          <button type="button" className="btn icon-btn hr-remove" aria-label={`Remove ${label}`} onClick={onRemove}>
            <Icon name="close" />
          </button>
        ) : null}
      </div>
      {error ? (
        <span id={`${id}-err`} className="pill st-error err-pill">
          {error}
        </span>
      ) : null}
    </div>
  );
}
